import React, { useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CanvasBoard, StickyNote, STICKY_COLORS, StickyColor } from '../components/canvas';
import { useBoardStore } from '../store/boardStore';
import { useAuthStore } from '../store/authStore';
import LoadingSpinner from '../components/LoadingSpinner';

const CanvasBoardPage: React.FC = () => {
  const { boardId } = useParams<{ boardId: string }>();
  const navigate = useNavigate();
  const currentUser = useAuthStore(state => (state as any).user);
  const {
    currentBoard,
    boards,
    loading,
    fetchBoard,
    fetchBoards,
    joinBoard,
    leaveBoard,
    createCanvasCard,
    updateCanvasCard,
    deleteCard,
    toggleCardVisibility,
  } = useBoardStore();

  useEffect(() => {
    if (boardId) {
      fetchBoard(boardId);
      joinBoard(boardId);
      fetchBoards(); // Fetch all boards for dropdown
    } else {
      navigate('/app/dashboard');
    }

    return () => {
      if (boardId) {
        leaveBoard(boardId);
      }
    };
  }, [boardId, fetchBoard, fetchBoards, joinBoard, leaveBoard, navigate]);

  // Set page title based on board title
  useEffect(() => {
    if (currentBoard?.title) {
      document.title = `${currentBoard.title} | Retrosive`;
    } else {
      document.title = 'Board | Retrosive';
    }
  }, [currentBoard?.title]);

  // Helper function to convert hex color string to StickyColor object
  const getStickyColor = useCallback((colorHex: string | undefined, fallbackIndex: number): StickyColor => {
    if (!colorHex) {
      return STICKY_COLORS[fallbackIndex % STICKY_COLORS.length];
    }
    
    // Try to find matching color in STICKY_COLORS by bg hex value
    const foundColor = STICKY_COLORS.find(sc => sc.bg.toLowerCase() === colorHex.toLowerCase());
    if (foundColor) {
      return foundColor;
    }
    
    // If not found, create a default StickyColor from the hex value
    return {
      name: 'Custom',
      bg: colorHex,
      border: colorHex,
      text: '#000000',
    };
  }, []);

  // Derive stickies from currentBoard - Single Source of Truth
  const stickies = useMemo((): StickyNote[] => {
    if (!currentBoard?.columns) {
      return [];
    }
    
    const allCards = currentBoard.columns.flatMap(column => column.cards);
    return allCards.map((card, index) => ({
      id: card.id,
      content: card.content,
      x: card.position_x ?? (100 + (index % 5) * 200),
      y: card.position_y ?? (100 + Math.floor(index / 5) * 150),
      width: card.width ?? 180,
      height: card.height ?? 140,
      color: getStickyColor(card.color, index),
      zIndex: card.z_index ?? index + 1,
      is_author_hidden: card.is_author_hidden,
      createdBy: card.created_by,
    }));
  }, [currentBoard, getStickyColor]);

  // Handle sticky creation - uses REST API via boardStore
  const handleStickyCreate = useCallback(async (sticky: StickyNote): Promise<string | null> => {
    if (!currentBoard?.columns?.[0]) {
      console.error('[handleStickyCreate] No column available. currentBoard:', currentBoard);
      return null;
    }
    
    try {
      // Get color hex value from StickyColor object or string
      const colorHex = typeof sticky.color === 'string' 
        ? sticky.color 
        : sticky.color?.bg || '#FBBF24';
      
      const columnId = currentBoard.columns[0].id;
      console.log('[handleStickyCreate] Creating card in column:', columnId);
      console.log('[handleStickyCreate] Sticky data:', {
        x: sticky.x,
        y: sticky.y,
        width: sticky.width,
        height: sticky.height,
        zIndex: sticky.zIndex,
        color: colorHex,
      });
      
      // Create card via REST API - returns the created card with real ID
      const createdCard = await createCanvasCard(columnId, {
        content: sticky.content || '',
        position_x: sticky.x,
        position_y: sticky.y,
        width: sticky.width,
        height: sticky.height,
        z_index: sticky.zIndex,
        color: colorHex,
      });
      
      if (createdCard) {
        console.log('[handleStickyCreate] Card created with ID:', createdCard.id);
        return createdCard.id;
      }
      
      return null;
    } catch (error) {
      console.error('[handleStickyCreate] Failed:', error);
      return null;
    }
  }, [currentBoard, createCanvasCard]);

  // Handle sticky update - uses REST API via boardStore
  const handleStickyUpdate = useCallback(async (id: string, updates: Partial<StickyNote>) => {
    try {
      const updateData: {
        content?: string;
        position_x?: number;
        position_y?: number;
        width?: number;
        height?: number;
        z_index?: number;
        color?: string;
      } = {};
      
      if (updates.content !== undefined) {
        updateData.content = updates.content;
      }
      if (updates.x !== undefined) {
        updateData.position_x = updates.x;
      }
      if (updates.y !== undefined) {
        updateData.position_y = updates.y;
      }
      if (updates.width !== undefined) {
        updateData.width = updates.width;
      }
      if (updates.height !== undefined) {
        updateData.height = updates.height;
      }
      if (updates.zIndex !== undefined) {
        updateData.z_index = updates.zIndex;
      }
      if (updates.color !== undefined) {
        const colorHex = typeof updates.color === 'string' 
          ? updates.color 
          : updates.color?.bg || '#FBBF24';
        updateData.color = colorHex;
      }
      
      if (Object.keys(updateData).length > 0) {
        await updateCanvasCard(id, updateData);
      }
    } catch (error) {
      console.error('[handleStickyUpdate] Failed:', error);
    }
  }, [updateCanvasCard]);

  // Handle sticky delete
  const handleStickyDelete = useCallback(async (id: string) => {
    try {
      await deleteCard(id);
    } catch (error) {
      console.error('[handleStickyDelete] Failed:', error);
    }
  }, [deleteCard]);

  // Handle visibility toggle
  const handleToggleVisibility = useCallback(async (id: string) => {
    await toggleCardVisibility(id);
  }, [toggleCardVisibility]);

  if (loading || !currentBoard) {
    return (
      <div className="h-screen w-screen flex items-center justify-center bg-gray-100">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  // Convert boards to dropdown format
  const boardList = boards.map(b => ({
    id: b.id,
    title: b.title,
  }));

  // Convert columns to the format expected by CanvasBoard
  const boardColumns = currentBoard.columns?.map(col => ({
    id: col.id,
    title: col.title,
    description: col.description,
    column_type: col.column_type,
    color: col.color,
    order: col.order,
  })) || [];

  return (
    <CanvasBoard
      boardId={currentBoard.id}
      boardTitle={currentBoard.title}
      teamId={currentBoard.team ?? undefined}
      onBack={() => navigate('/app/dashboard')}
      initialStickies={stickies}
      columns={boardColumns}
      onStickyCreate={handleStickyCreate}
      onStickyUpdate={handleStickyUpdate}
      onStickyDelete={handleStickyDelete}
      onStickyToggleVisibility={handleToggleVisibility}
      currentUserId={currentUser?.id}
      boards={boardList}
    />
  );
};

export default CanvasBoardPage;
